import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useCustomTranslations } from '../../../../hooks/useCustomTranslations';
import { usePages } from '../../../../hooks/usePages';
import { PageData } from '../../../../interfaces/interfaces';
import { TeamHeroMenuComponentInterface } from '../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { ConditionalWrapper } from '../../../common/ConditionalWrapper';
import Link from 'next/link';

const SlideMenu = ({
  className,
  items,
  textColor,
  backgroundColor,
}: {
  className: string;
  items?: TeamHeroMenuComponentInterface['items'];
  textColor?: string;
  backgroundColor?: string;
}) => {
  const [isMenuOverflowing, setIsMenuOverflowing] = useState(false);
  const firstElement = useRef<HTMLLIElement>(null);
  const slider = useRef<HTMLLIElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const event = useSelector((state) => state.common.event);
  const pages = useSelector((state) => state.common.pages);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);
  const router = useRouter();

  const {
    query: { pagename },
  } = router;

  const { filterEventPages, findPageBySlugOrName, switchPageWithPageData, getUrlForPageData } = usePages();
  const { ct } = useCustomTranslations();
  const { t } = useTranslation();
  const liveEventsPages = useMemo(() => filterEventPages(pages), [pages]);

  useEffect(() => {
    // cant figure out why offsetLeft initially is not the same after couple of milliseconds
    // so i put in event loop
    setTimeout(() => {
      if (slider.current && firstElement.current) setSliderStyles(slider.current, firstElement.current);
      if (menuListRef.current) {
        const isOverflowing = menuListRef.current.offsetWidth + 32 < menuListRef.current.scrollWidth;
        setIsMenuOverflowing(isOverflowing);
      }
    }, 500);
  }, [pagename]);

  const screenResizeCb = useCallback(() => {
    if (!menuListRef.current) return;
    const isOverflowing = menuListRef.current.offsetWidth + 32 < menuListRef.current.scrollWidth;
    setIsMenuOverflowing(isOverflowing);
  }, [menuListRef, isMenuOverflowing]);

  useEffect(() => {
    window.addEventListener('resize', screenResizeCb);
    return () => {
      window.removeEventListener('resize', screenResizeCb);
    };
  }, []);

  useEffect(() => {
    if (isMenuOverflowing && menuListRef) {
      let scrollWidth = 0;
      liveEventsPages.map((p, i) => {
        const itemWidth = menuListRef?.current?.children[i].clientWidth ?? 0;
        if (p.name !== pagename) scrollWidth += itemWidth;
        else if (menuListRef.current && scrollWidth + itemWidth > menuListRef.current?.offsetWidth) {
          menuListRef.current?.scroll({ left: scrollWidth, behavior: 'smooth' });
        }
      });
    }
  }, [isMenuOverflowing, menuListRef]);

  function changePage({ currentTarget }: React.MouseEvent<HTMLLIElement>, eventPage: PageData) {
    if (!event) return;
    if (templateEditMode) {
      toast(t('pagebuilder:edit-mode-page-switch'));
      return;
    }
    if (slider.current) setSliderStyles(slider.current, currentTarget);

    if (!eventPage.linkSettings?.url) {
      const pageData = findPageBySlugOrName(eventPage.slug && eventPage.slug !== '' ? eventPage.slug : eventPage.name, liveEventsPages);
      console.log(pageData);
      if (!pageData) return router.push(getUrlForPageData(eventPage, event.slug));
      switchPageWithPageData(pageData, event.slug);
      if (window.pageYOffset > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function setSliderStyles(sliderElement: HTMLLIElement, target: HTMLLIElement) {
    sliderElement.innerText = target.innerText;
    sliderElement.style.left = `${target.offsetLeft}px`;
    sliderElement.style.minWidth = `${target.offsetWidth}px`;
  }

  const scrollToValue = (direction: 'right' | 'left') => {
    if (!menuListRef.current) return;
    if (direction === 'right')
      menuListRef.current.scrollTo({ left: menuListRef.current.scrollLeft + (menuListRef.current.clientWidth * 2) / 3, behavior: 'smooth' });
    else menuListRef.current.scrollTo({ left: menuListRef.current.scrollLeft - (menuListRef.current.clientWidth * 2) / 3, behavior: 'smooth' });
  };
  return (
    <div className={`flex text-white justify-center align-center relative listWrapper ${className || ''}`}>
      <div className="relative">
        {isMenuOverflowing && (
          <>
            <div
              className="hover:opacity-80 absolute left-0 h-full z-10 opacity-30 hidden @md:grid place-items-center transition-opacity"
              style={{
                backgroundImage: 'linear-gradient(to left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))',
                transition: 'opacity 100ms ease-out',
              }}>
              <IconButton onClick={() => scrollToValue('left')} size="large">
                <ChevronLeft fontSize="medium" style={{ color: '#C3C6C5' }} />
              </IconButton>
            </div>
            <div
              className="hover:opacity-80 absolute right-0 h-full z-10 opacity-30 hidden @md:grid place-items-center transition-opacity"
              style={{
                backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))',
              }}>
              <IconButton onClick={() => scrollToValue('right')} size="large">
                <ChevronRight fontSize="medium" style={{ color: '#C3C6C5' }} />
              </IconButton>
            </div>
          </>
        )}
        <ul
          ref={menuListRef}
          style={{ maxWidth: '75cqw', overflow: 'hidden' }}
          className={`flex flex-col @md:flex-row items-center relative rounded-md text-center list-none shadow-md `}>
          {!!event &&
            liveEventsPages.map((ep, index, allItems) => {
              const isActive = pagename === ep.name || pagename === ep.slug;
              return (
                <span
                  key={`slick-menu-link-${ep._id}`}
                  className={`h-full w-full @md:w-auto filter hover:shadow-2xl transition-shadow duration-200 flex items-center px-4 py-2 cursor-pointer ${
                    index < allItems.length - 1 ? 'border-b-2 @md:border-b-0' : ''
                  } ${isActive ? 'bg-buttons-main' : ''}`}
                  style={
                    isActive
                      ? {}
                      : {
                          backgroundColor: !isActive ? event?.template?.complementColor || 'var(--vystem-button-text)' : '',
                          color: event?.template?.complementButtonTextColor || 'var(--vystem-button-text)',
                        }
                  }>
                  {ep.linkSettings?.url ? (
                    <Link target={ep.linkSettings.openInNewTab ? '_blank' : '_self'} href={ep.linkSettings?.url}>
                      <li className="relative w-full text-center" ref={pagename === ep.name ? firstElement : null}>
                        {ep.template?.buttonText || ct(ep.name, ep)}
                      </li>
                    </Link>
                  ) : (
                    <li
                      key={'slide-menu-' + index}
                      id={'slide-menu-' + index}
                      className="relative w-full text-center"
                      ref={pagename === ep.name ? firstElement : null}
                      onClick={(e) => changePage(e, ep)}>
                      {ep.template?.buttonText || ct(ep.name, ep)}
                    </li>
                  )}
                </span>
              );
            })}
          {items
            ?.slice()
            .sort((a, b) => a.sortIndex - b.sortIndex)
            .map((item, index) => (
              <span
                key={`slick-menu-link-${index}`}
                className={`h-full  w-full @md:w-auto filter hover:shadow-2xl transition-shadow duration-200 flex items-center border-b-2 @md:border-b-0`}
                style={{ color: textColor, backgroundColor }}>
                <ConditionalWrapper
                  condition={!!item.url}
                  wrapper={(children) => (
                    <Link target={item.openInNewTab ? '_blank' : '_self'} href={item.url ?? ''}>
                      {children}
                    </Link>
                  )}>
                  <li>{item.text}</li>
                </ConditionalWrapper>
              </span>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default SlideMenu;
